import { Component, EventEmitter, OnInit, OnDestroy, Input, Output, ViewChild } from '@angular/core';
import { SubSink } from 'subsink2';
import { forkJoin, iif, Observable, of, Subject, zip } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Pagination, ApiResponseModel, bl_fi_generic_doc_hdr_RowClass, GenericDocContainerModel, LocationService } from 'blg-akaun-ts-lib';
import { AppConfig } from '../../visa';
import { PaginationComponent } from '../../utilities/pagination/pagination.component';
import { SearchEvent } from '../../models/query.model';
import { formatMoneyInList } from '../../format.utils';
import moment from 'moment';

@Component({
  selector: 'app-line-container-ko-by',
  templateUrl: './line-container-ko-by.component.html',
  styleUrls: ['./line-container-ko-by.component.css']
})
export class LineContainerKoByComponent implements OnInit, OnDestroy {

  @Input() localState: any;
  @Input() hdr: bl_fi_generic_doc_hdr_RowClass;
  @Input() searchModel;
  @Input() advSearchId: string;
  @Input() service;
  @Input() serverDocTypeDoc2: string;
  @Input() itemType: string;
  @Input() additionalColDef;

  @Output() addLineItem = new EventEmitter();

  protected subs = new SubSink();

  gridApi;
  // Left null: the guid list SubQueryService used to resolve is gone, and the
  // `line_guids` criterion below resolves to '' exactly as it did with no search.
  SQLGuids: string[] = null;
  // Criteria from the search bar, appended to the datasource's criteria.
  searchCriteria: any[] = [];
  /**
   * Maps advanced-search form fields onto the criteria the generic-document line
   * endpoints accept. Override per applet when a search model names its fields
   * differently.
   *
   * `contains` says who supplies the ILIKE wildcards, and it is not decorative:
   * GenericDocumentUow.generateWhereConditionByCriteria is inconsistent about it
   * in the same method. `doc_reference_like` and friends are emitted as
   *   ILIKE '%' || :doc_reference_like || '%'
   * but these three are emitted as
   *   ILIKE :server_doc_1_like
   * with no wildcards at all. Sending a bare value to the latter would turn what
   * used to be `hdr.server_doc_1 ILIKE '%v%'` into an exact match, so the pattern
   * is built here.
   */
  @Input() searchFieldCriteriaMap: { [formField: string]: { columnName: string, contains?: boolean } } = {
    docNo: { columnName: 'server_doc_1_like', contains: true },
    itemCode: { columnName: 'line_item_code_like', contains: true },
    itemName: { columnName: 'line_item_name_like', contains: true },
  };

  pagination = new Pagination();
  snapshot: any;
  totalCount: number;
  totalRecords$: Subject<number> = new Subject<number>();
  emptyGrid: boolean;
  criteriaList: { columnName: string; operator: string; value: string; }[];
  enableListing;
  showCondition: string;
  
  @ViewChild(PaginationComponent) paginationComp: PaginationComponent;

  defaultColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
    minWidth: 200,
    flex: 2,
    sortable: true,
    resizable: true,
    suppressCsvExport: true
  };
  
  columnsDefs = [
    { headerName: 'Doc No.', field: 'doc_number', cellStyle: () => ({ 'text-align': 'left' }), maxWidth: 100 },
    { headerName: 'Server Doc Type', field: 'server_doc_type_hdr', cellStyle: () => ({ 'text-align': 'left' }) },    
    { headerName: 'Txn Date', field: 'date_txn', cellStyle: () => ({ 'text-align': 'left' }), maxWidth: 100, 
      valueFormatter: params => params.value ? moment(params.value).format('YYYY-MM-DD') : null },
    { headerName: 'Item Code', field: 'item_code', cellStyle: () => ({ 'text-align': 'left' }) },
    { headerName: 'Item Name', field: 'item_name', cellStyle: () => ({ 'text-align': 'left' }) },
    // { headerName: 'Delivered Qty', field: 'del_qty', type: 'numericColumn' },
    { headerName: 'UOM', field: 'uom', cellStyle: () => ({ 'text-align': 'left' }), maxWidth: 100 },
    { headerName: 'Unit Price (Inc. of Tax)', field: 'unit_price_txn', type: 'numericColumn', maxWidth: 150,
      valueFormatter: (params) => params.value ? formatMoneyInList(params.value) : null },
    { headerName: 'Base Qty.', field: 'order_qty', type: 'numericColumn', maxWidth: 100 },
    { headerName: 'Bal. Qty.', field: 'open_qty', type: 'numericColumn', maxWidth: 100 },
    { headerName: 'Knockoff Qty.', type: 'numericColumn', maxWidth: 150,
      valueGetter: (params) => { return params.data.ko_qty; }
    },
    // { headerName: 'Status', field: 'status', cellStyle: () => ({ 'text-align': 'left' }) }
  ];

  constructor(
    private locationService: LocationService
    ) {
  }

  ngOnInit() {
    if (this.additionalColDef){
      this.columnsDefs = [...this.columnsDefs,...this.additionalColDef];
    }

    this.enableListing = ((this.hdr.server_doc_type === "INTERNAL_OUTBOUND_STOCK_TRANSFER" || 
                           this.hdr.server_doc_type === "INTERNAL_INBOUND_STOCK_TRANSFER") &&
                           this.hdr.guid_comp) || 
                          (this.hdr.doc_entity_hdr_guid && this.hdr.guid_comp)
    
    this.showCondition = (this.hdr.server_doc_type === "INTERNAL_OUTBOUND_STOCK_TRANSFER" || this.hdr.server_doc_type === "INTERNAL_INBOUND_STOCK_TRANSFER")? 
    'location sending and location receiving':
    'an entity and a branch';
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    this.setGridData();
  }

  clear() {
    const dataSource = {
      getRows(params: any) {
        params.successCallback([], 0);
      }
    };
    this.gridApi.setServerSideDatasource(dataSource);
  }

  setGridData(criteria?: any) {
    this.snapshot = null;
    const apiVisa = AppConfig.apiVisa;

    const datasource = {
      getRows: grid => {
        const sortModel = grid.request.sortModel;
        const sortCriteria = [];
        if (sortModel.length > 0) {
          sortModel.forEach(element => {
            const columnName = element.colId.split(".")[1]
            sortCriteria.push({ columnName: 'orderBy', value: columnName })
          });
          sortCriteria.push({ columnName: 'order', value: sortModel[0].sort.toUpperCase() })
        }
        // sortCriteria.push({ columnName: 'doc_entity_hdr_guid', value: this.hdr.doc_entity_hdr_guid? this.hdr.doc_entity_hdr_guid:'' })
        // sortCriteria.push({ columnName: 'guid_comp', value: this.hdr.guid_comp? this.hdr.guid_comp:'' })
        if (this.hdr.server_doc_type === "INTERNAL_OUTBOUND_STOCK_TRANSFER"){
          sortCriteria.push({ columnName: 'guid_comp', value: this.hdr.guid_comp? this.hdr.guid_comp:'' })
          sortCriteria.push({columnName: 'guid_store', value: this.hdr.guid_store? this.hdr.guid_store:''})
          sortCriteria.push({columnName: "line_guids", operator: "=", value: this.SQLGuids? this.SQLGuids.slice(grid.request.startRow, grid.request.endRow).toString(): ''})
        }
        else if (this.hdr.server_doc_type === "INTERNAL_INBOUND_STOCK_TRANSFER"){
          sortCriteria.push({ columnName: 'guid_comp', value: this.hdr.guid_comp? this.hdr.guid_comp:'' })
          sortCriteria.push({columnName: 'guid_store_2', value: this.hdr.guid_store_2? this.hdr.guid_store_2:''})
          sortCriteria.push({columnName: "line_guids", operator: "=", value: this.SQLGuids? this.SQLGuids.slice(grid.request.startRow, grid.request.endRow).toString(): ''})
        }
        else{
          sortCriteria.push({ columnName: 'doc_entity_hdr_guid', value: this.hdr.doc_entity_hdr_guid? this.hdr.doc_entity_hdr_guid:'' })
          sortCriteria.push({ columnName: 'guid_comp', value: this.hdr.guid_comp? this.hdr.guid_comp:'' })
          sortCriteria.push({columnName: "line_guids", operator: "=", value: this.SQLGuids? this.SQLGuids.slice(grid.request.startRow, grid.request.endRow).toString(): ''})
        }
        const limit = grid.request.endRow - grid.request.startRow;
        // const pagination = new Pagination(0, limit, criteria, sortCriteria, this.snapshot);
        const offset = this.SQLGuids ? 0 : grid.request.startRow;
        // Search criteria are WHERE conditions, so they belong in the conditional
        // criteria (3rd argument), not the sort criteria.
        const conditionalCriteria = (criteria || []).concat(this.searchCriteria || []);
        const pagination = new Pagination(offset, limit, conditionalCriteria, sortCriteria);
        
        this.subs.sink = this.service.getGenericDocHdrLineLinkByCriteria(
          pagination,
          apiVisa).pipe(
            mergeMap((b:any) => {
              const source: Observable<GenericDocContainerModel>[]=[];
              b.data.forEach((doc)=>{
                source.push(
                  zip(
                    this.locationService
                      .getByGuid(doc.bl_fi_generic_doc_line?.guid_store?.toString(),apiVisa)
                        .pipe(catchError((err) => of(err))),
                    this.locationService
                      .getByGuid(doc.bl_fi_generic_doc_line?.guid_store_2?.toString(),apiVisa)
                        .pipe(catchError((err) => of(err))),
                  )
                  .pipe(
                    map(([loc_1, loc_2]) =>{
                      console.log("this is in ko by",loc_1.data.bl_inv_mst_location?.name)
                      // console.log("this is in ko by 2",loc_2.data.bl_inv_mst_location?.name)
                      console.log("this is error??",loc_1.error? '': loc_1.data.bl_inv_mst_location?.name)
                      console.log("this is error2??",loc_2.error? 'error2': loc_2.data.bl_inv_mst_location?.name)

                      doc = Object.assign(
                        {
                          location1: loc_1.error? '': loc_1.data.bl_inv_mst_location?.name,
                          location2: loc_2.error? '': loc_2.data.bl_inv_mst_location?.name
                        },
                        doc);
                      return doc;
                    })
                  )
                )
              });
              return iif(
                () => b.data.length > 0,
                forkJoin(source).pipe(
                  map((b_inner) => {
                    b.data = b_inner;
                    return b;
                  })
                ),
                of(b)
              );
            })
          ).subscribe(
            (res) => {
              console.log("this is the res", res.data)
              if (res.data.length > 0) {
                this.snapshot = res.data[res.data.length - 1].bl_fi_generic_doc_hdr.guid.toString();
              }

              let newArrData = new Array;
              newArrData = [...res.data].map((doc) => {

                let aggContra = 0; // aggregate Contra
                // doc.bl_fi_generic_doc_link?.forEach(link => {
                //   if(link.guid_doc_1_hdr === doc.bl_fi_generic_doc_hdr.guid &&
                //     link.guid_doc_1_line === doc.bl_fi_generic_doc_line[0].guid && link.txn_type === 'IPGRN_IPI') {
                //       let contra = Number(link.quantity_signum) * Number(link.quantity_contra);
                //       aggContra += contra;
                //     }
                // })

                doc.bl_fi_generic_doc_links?.forEach(link => {
                  if(
                    // link.server_doc_type_doc_1_line === "INTERNAL_PURCHASE_INVOICE" &&
                    // link.server_doc_type_doc_2_line === "INTERNAL_PURCHASE_GOODS_RECEIVED_NOTE" &&
                    link.server_doc_type_doc_2_line === this.serverDocTypeDoc2 &&
                    link.guid_doc_2_line === doc.bl_fi_generic_doc_line.guid &&
                    link.guid_doc_2_hdr === doc.bl_fi_generic_doc_hdr.guid
                  ) {
                    let contra = Number(link.quantity_signum) * Number(link.quantity_contra);
                    aggContra += contra;
                  }
                })

                let newObj = null;
                newObj = {
                  item_type: this.itemType,
                  hdr_guid: doc.bl_fi_generic_doc_hdr.guid,
                  server_doc_type_hdr: doc.bl_fi_generic_doc_hdr.server_doc_type,
                  line_guid: doc.bl_fi_generic_doc_line.guid,
                  server_doc_type_line: doc.bl_fi_generic_doc_line.server_doc_type,
                  doc_number: doc.bl_fi_generic_doc_hdr.server_doc_1,

                  item_guid: doc.bl_fi_generic_doc_line.item_guid,
                  item_code: doc.bl_fi_generic_doc_line.item_code,
                  item_name: doc.bl_fi_generic_doc_line.item_name,
                  order_qty: doc.bl_fi_generic_doc_line.quantity_base,
                  open_qty: Number(doc.bl_fi_generic_doc_line.quantity_base) + aggContra,
                  unit_price_std: doc.bl_fi_generic_doc_line.unit_price_std,
                  unit_price_txn: doc.bl_fi_generic_doc_line.unit_price_txn,
                  tax_gst_code: doc.bl_fi_generic_doc_line.tax_gst_code,
                  tax_gst_rate: doc.bl_fi_generic_doc_line.tax_gst_rate,
                  amount_tax_gst: doc.bl_fi_generic_doc_line.amount_tax_gst,
                  tax_wht_code: doc.bl_fi_generic_doc_line.tax_wht_code,
                  tax_wht_rate: doc.bl_fi_generic_doc_line.tax_wht_rate,
                  amount_tax_wht: doc.bl_fi_generic_doc_line.amount_tax_wht,
                  amount_discount: doc.bl_fi_generic_doc_line.amount_discount,
                  amount_net: doc.bl_fi_generic_doc_line.amount_net,
                  amount_std: doc.bl_fi_generic_doc_line.amount_std,
                  amount_txn: doc.bl_fi_generic_doc_line.amount_txn,
                  item_remarks: doc.bl_fi_generic_doc_line.item_remarks,
                  item_txn_type: doc.bl_fi_generic_doc_line.item_txn_type,
                  guid_dimension: doc.bl_fi_generic_doc_line.guid_dimension,
                  guid_profit_center: doc.bl_fi_generic_doc_line.guid_profit_center,
                  guid_project: doc.bl_fi_generic_doc_line.guid_project,
                  guid_segment: doc.bl_fi_generic_doc_line.guid_segment,
                  item_property_json: doc.bl_fi_generic_doc_line.item_property_json,
                  line_property_json: doc.bl_fi_generic_doc_line.line_property_json,
                  txn_type: doc.bl_fi_generic_doc_line.txn_type,
                  uom: doc.bl_fi_generic_doc_line.uom,
                  uom_to_base_ratio: doc.bl_fi_generic_doc_line.uom_to_base_ratio,
                  qty_by_uom: doc.bl_fi_generic_doc_line.qty_by_uom,
                  unit_price_std_by_uom: doc.bl_fi_generic_doc_line.unit_price_std_by_uom,
                  unit_price_txn_by_uom: doc.bl_fi_generic_doc_line.unit_price_txn_by_uom,
                  unit_disc_by_uom: doc.bl_fi_generic_doc_line.unit_disc_by_uom,
                  status: null,
                  date_txn: doc.bl_fi_generic_doc_line.date_txn,
                  ko_qty: Number(doc.bl_fi_generic_doc_line.quantity_base) + aggContra,
                  tracking_id: doc.bl_fi_generic_doc_line.tracking_id,
                  doc_ref: doc.bl_fi_generic_doc_hdr.doc_reference,
                  doc_desc: doc.bl_fi_generic_doc_hdr.doc_desc,
                  location1: doc.location1,
                  location2: doc.location2
                  // serial_no: doc.bl_fi_generic_doc_line[0].serial_no,
                  // bin_no: doc.bl_fi_generic_doc_line[0].bin_no,
                  // batch_no: doc.bl_fi_generic_doc_line[0].batch_no,
                }
                return newObj
              })

              if (this.paginationComp.currentPage > this.paginationComp.totalPage.value) {
                this.paginationComp.firstPage()
              }

              // Calculate totalRecords if end reached.
              const start = grid.request.startRow;
              const end = grid.request.endRow;
              const totalRecords = newArrData.length < (end - start) ? start + newArrData.length : null;

              if (!this.totalCount && totalRecords) {
                this.totalCount = totalRecords;
                this.setTotalRecordCount(totalRecords);
              }

              grid.successCallback(newArrData, totalRecords);

              this.gridApi.forEachNode(a => {
                if (a.data.item_guid === this.localState.selectedItem) {
                  a.setSelected(true);
                }
              });
            }, err => {
              console.log(err);
              grid.failCallback();

            })
      }
    };
    this.gridApi.setServerSideDatasource(datasource);
  }

  setTotalRecordCount(totalCount: number) {
    this.totalRecords$.next(totalCount);
  }

  // onSearch(e) {
  //   if (!e.isEmpty) {
  //     this.searchQuery(e.queryString);
  //     console.log('searchQuery', e);

  //   } else {
  //     this.criteriaList = [];
  //     this.emptyGrid = false;
  //     this.setGridData();
  //     this.setTotalRecordCount(this.totalCount);
  //   }
  // }
  /**
   * Handles a search emitted by <app-advanced-search-lines>.
   *
   * Replaces the SubQueryService round-trip: the search bar's raw SELECT used to
   * be posted to /subquery, and the resolved line guids were paged through the
   * `line_guids` criterion. The listing now sends criteria straight to
   * getGenericDocHdrLineLinkByCriteria, so the grid pages server-side normally
   * instead of slicing a client-side guid list.
   *
   * The search bar is set to `returnFormOnSubject`, so it emits the raw form on
   * `searchV2` rather than SQL on `search` -- the same pair <app-advanced-search>
   * already offers. That keeps the mapping from form field to column here, in the
   * listing that knows its own endpoint, and means the applet-supplied
   * `searchModel` needs no callbacks of any kind: a plain SearchModelV2 works.
   *
   * Keyword search maps to `line_search_word`, which
   * GenericDocumentUow.generateWhereConditionByCriteria (the where-clause builder
   * behind the hdr-line-link query this listing calls) applies as a bound ILIKE
   * over hdr.server_doc_1, line.item_code and line.item_name -- the columns this
   * grid shows.
   */
  onSearch(e: SearchEvent) {
    const criteria = this.buildSearchCriteria(e);
    if (criteria === null) {
      // Unmappable advanced search - leave the current result set alone.
      return;
    }
    this.searchCriteria = criteria;
    this.paginationComp.firstPage();
    this.gridApi.refreshServerSideStore();
  }

  /**
   * Maps the emitted search form onto criteria, or returns null when a field the
   * user filled in has no mapping - in which case the listing is deliberately
   * left as it is rather than reloaded with no filter, because showing every row
   * to someone who asked for a filtered one is the more dangerous failure.
   *
   * `searchFieldCriteriaMap` is the field -> criterion mapping. It defaults to
   * the fields the generic-document line search models use, and is an @Input so
   * an applet whose model names its fields differently can override it without a
   * library change.
   */
  private buildSearchCriteria(e: SearchEvent): any[] {
    if (!e || e.isEmpty) {
      return [];
    }
    if (e.basicSearchMode) {
      return [{ columnName: 'line_search_word', operator: '=', value: (e.basicQuery || '').trim() }];
    }

    const form = e.form || {};
    const criteria: any[] = [];
    const unmapped: string[] = [];

    Object.keys(form).forEach(field => {
      const value = form[field];
      if (value === null || value === undefined || String(value).trim() === '') {
        return;
      }
      const mapping = this.searchFieldCriteriaMap[field];
      if (!mapping || !mapping.columnName) {
        unmapped.push(field);
        return;
      }
      const trimmed = String(value).trim();
      criteria.push({
        columnName: mapping.columnName,
        operator: '=',
        value: mapping.contains ? `%${trimmed}%` : trimmed
      });
    });

    if (unmapped.length) {
      console.error(
        'line-container-ko-by: advanced search was not applied and the listing was left unchanged. '
        + 'No criteria mapping for: ' + unmapped.join(', ') + '. Pass [searchFieldCriteriaMap] '
        + 'to map those fields onto the criteria this endpoint accepts.'
      );
      return null;
    }
    return criteria;
  }

  onRowClicked(item) {
    // this.addLineItem.emit(item);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

}
